/*
  Startup Rev — Venue page
  - Lazy-loads MapLibre (vendored in assets) when the map scrolls near the
    viewport, then places day-badged pins and a dashed route between them.
  - Uses OpenFreeMap vector tiles with English labels. OSM raster tiles
    print Skopje in Macedonian; vector styles can prefer name:en / Latin.
  - Keeps the rail beside the map in sync: the active stop expands to show
    its address and directions; picking a stop flies the map to its pin.
  Progressive enhancement: without JS the page still lists every venue with
  "open in maps" links, and the main venue's links are already expanded.
*/
(() => {
  if (window.__srVenueScriptLoaded) return;
  window.__srVenueScriptLoaded = true;

  const OPENFREEMAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';
  const ENGLISH_TEXT_FIELD = [
    'coalesce',
    ['get', 'name:latin'],
    ['get', 'name_en'],
    ['get', 'name:en'],
    ['get', 'name_int'],
    ['get', 'name'],
  ];

  const selectAll = (root, selector) => Array.from(root.querySelectorAll(selector));
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const escapeHtml = (value) =>
    String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const loadedAssets = {};

  const loadStyle = (href) =>
    new Promise((resolve, reject) => {
      if (!href) return resolve();
      if (loadedAssets[href] || document.querySelector(`link[href="${href}"]`)) {
        loadedAssets[href] = true;
        return resolve();
      }
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = () => {
        loadedAssets[href] = true;
        resolve();
      };
      link.onerror = () => reject(new Error(`Could not load ${href}`));
      document.head.appendChild(link);
    });

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      if (!src) return resolve();
      if (loadedAssets[src] || window.maplibregl) {
        loadedAssets[src] = true;
        return resolve();
      }
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => {
        loadedAssets[src] = true;
        resolve();
      };
      script.onerror = () => reject(new Error(`Could not load ${src}`));
      document.head.appendChild(script);
    });

  /* ---------- Map ---------- */

  const directionsLinks = (pin) => {
    const google = `https://www.google.com/maps/dir/?api=1&destination=${pin.lat},${pin.lng}`;
    const apple = `https://maps.apple.com/?daddr=${pin.lat},${pin.lng}`;
    const details = pin.anchor ? `<a href="#stop-${escapeHtml(pin.anchor)}">Details</a>` : '';
    return `
      <a href="${google}" target="_blank" rel="noopener noreferrer">Google Maps</a>
      <a href="${apple}" target="_blank" rel="noopener noreferrer">Apple Maps</a>
      ${details}
    `;
  };

  const popupHtml = (pin) => `
    <div class="sr-venue-popup__day">${escapeHtml(pin.day)}</div>
    <div class="sr-venue-popup__name">${escapeHtml(pin.name)}</div>
    ${pin.address ? `<div class="sr-venue-popup__address">${escapeHtml(pin.address)}</div>` : ''}
    <div class="sr-venue-popup__links">${directionsLinks(pin)}</div>
  `;

  const preferEnglishLabels = (style) => {
    if (!style || !style.layers) return style;
    style.layers.forEach((layer) => {
      const layout = layer.layout;
      if (!layout || !layout['text-field']) return;
      if (JSON.stringify(layout['text-field']).includes('["get","ref"]')) return;
      layout['text-field'] = ENGLISH_TEXT_FIELD;
    });
    return style;
  };

  const loadEnglishStyle = (url) =>
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Could not load map style.');
        return response.json();
      })
      .then(preferEnglishLabels);

  const openMarkerPopup = (marker) => {
    const popup = marker.getPopup();
    if (!popup) return;
    if (!popup.isOpen()) marker.togglePopup();
  };

  const closeOtherPopups = (markers, keep) => {
    markers.forEach((marker) => {
      if (marker === keep) return;
      const popup = marker.getPopup();
      if (popup && popup.isOpen()) popup.remove();
    });
  };

  const buildMap = (canvas, pins, options) => {
    const maplibregl = window.maplibregl;
    if (typeof maplibregl.supported === 'function' && !maplibregl.supported()) {
      throw new Error('MapLibre is not supported.');
    }

    const first = pins[0];
    const map = new maplibregl.Map({
      container: canvas,
      style: options.style,
      center: [first.lng, first.lat],
      zoom: 12,
      minZoom: 10,
      maxZoom: 19,
      scrollZoom: false,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
    });

    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false, visualizePitch: false }),
      'top-left'
    );
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');
    map.touchZoomRotate.disableRotation();

    const markers = new Map();
    const bounds = new maplibregl.LngLatBounds();

    pins.forEach((pin) => {
      bounds.extend([pin.lng, pin.lat]);

      const el = document.createElement('div');
      el.className = `sr-venue-pin${pin.main ? ' is-main' : ''}`;
      el.setAttribute('role', 'img');
      el.setAttribute('aria-label', pin.name);
      el.innerHTML = `<span class="sr-venue-pin__badge">${escapeHtml(pin.badge)}</span><span class="sr-venue-pin__stem"></span>`;

      const popup = new maplibregl.Popup({
        className: 'sr-venue-popup',
        maxWidth: '280px',
        offset: 62,
        closeButton: true,
      }).setHTML(popupHtml(pin));

      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([pin.lng, pin.lat])
        .setPopup(popup)
        .addTo(map);

      el.addEventListener('click', () => {
        closeOtherPopups(markers, marker);
      });

      markers.set(pin.id, marker);
    });

    const onReady = () => {
      if (options.showRoute && pins.length > 1 && !map.getSource('sr-venue-route')) {
        map.addSource('sr-venue-route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: pins.map((pin) => [pin.lng, pin.lat]),
            },
          },
        });
        map.addLayer({
          id: 'sr-venue-route',
          type: 'line',
          source: 'sr-venue-route',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': '#a6004f',
            'line-width': 2,
            'line-opacity': 0.75,
            'line-dasharray': [0.4, 1.6],
          },
        });
      }
      const fitted = map.cameraForBounds(bounds, { padding: 56, maxZoom: 15 });
      if (fitted) {
        map.jumpTo({
          center: fitted.center,
          zoom: Math.max(map.getMinZoom(), fitted.zoom - 1),
          bearing: fitted.bearing,
        });
      } else {
        map.fitBounds(bounds, { padding: 56, maxZoom: 15, duration: 0 });
      }
      map.resize();
    };

    if (map.loaded()) onReady();
    else map.once('load', onReady);

    return { map, markers };
  };

  const initMapSection = (root) => {
    const canvas = root.querySelector('[data-sr-venue-map]');
    if (!canvas || canvas.dataset.srVenueMapInit === 'true') return;
    canvas.dataset.srVenueMapInit = 'true';

    let pins = [];
    try {
      const raw = JSON.parse(canvas.querySelector('[data-sr-venue-pins]').textContent);
      pins = raw
        .map((pin) => ({ ...pin, lat: parseFloat(pin.lat), lng: parseFloat(pin.lng) }))
        .filter((pin) => Number.isFinite(pin.lat) && Number.isFinite(pin.lng));
    } catch (error) {
      pins = [];
    }
    if (!pins.length) return;

    const options = {
      styleUrl: canvas.dataset.styleUrl || OPENFREEMAP_STYLE,
      showRoute: canvas.dataset.showRoute !== 'false',
    };

    const items = selectAll(root, '[data-sr-pin-item]');
    let state = null;
    let booting = null;

    const setActive = (id) => {
      items.forEach((item) => {
        const active = item.dataset.srPinItem === id;
        item.classList.toggle('is-active', active);
        const button = item.querySelector('[data-sr-pin-focus]');
        if (button) button.setAttribute('aria-expanded', active ? 'true' : 'false');
      });
      if (!state) return;
      state.markers.forEach((marker, markerId) => {
        const el = marker.getElement();
        if (el) el.classList.toggle('is-active', markerId === id);
      });
    };

    const boot = () => {
      if (booting) return booting;
      booting = loadStyle(canvas.dataset.maplibreCss)
        .then(() => loadScript(canvas.dataset.maplibreJs))
        .then(() => {
          if (!window.maplibregl) throw new Error('MapLibre did not initialise.');
          return loadEnglishStyle(options.styleUrl);
        })
        .then((style) => {
          state = buildMap(canvas, pins, { ...options, style });
          state.markers.forEach((marker, id) => {
            marker.getElement().addEventListener('click', () => setActive(id));
          });
          canvas.classList.add('is-ready');
          return state;
        })
        .catch(() => {
          canvas.classList.add('is-failed');
          return null;
        });
      return booting;
    };

    // Bring the map into view when it is mostly off-screen (the list sits below it on phones).
    const revealCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const visible = Math.max(0, Math.min(rect.bottom, viewport) - Math.max(rect.top, 0));
      if (rect.height === 0 || visible / rect.height >= 0.6) return;
      canvas.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
    };

    const focusPin = (id) => {
      const pin = pins.find((entry) => entry.id === id);
      if (!pin) return;
      setActive(id);
      revealCanvas();
      boot().then((ready) => {
        if (!ready) return;
        const marker = ready.markers.get(id);
        if (!marker) return;
        const camera = { center: [pin.lng, pin.lat], zoom: 16 };
        if (reduceMotion()) {
          ready.map.jumpTo(camera);
        } else {
          ready.map.flyTo({ ...camera, duration: 900 });
        }
        closeOtherPopups(ready.markers, marker);
        openMarkerPopup(marker);
      });
    };

    selectAll(root, '[data-sr-pin-focus]').forEach((button) => {
      button.addEventListener('click', () => focusPin(button.dataset.srPinFocus));
    });

    let observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries, obs) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          obs.disconnect();
          boot();
        },
        { rootMargin: '400px 0px' }
      );
      observer.observe(canvas);
    } else {
      boot();
    }

    root.addEventListener(
      'sr:cleanup',
      () => {
        if (observer) observer.disconnect();
        if (state && state.map) state.map.remove();
        state = null;
        delete canvas.dataset.srVenueMapInit;
      },
      { once: true }
    );
  };

  /* ---------- Boot ---------- */

  const initAll = (scope = document) => {
    selectAll(scope, '[data-sr-venue-map-section]').forEach(initMapSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
